import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Tag as TagIcon, Search, Pencil, Trash2 } from "lucide-react";
import "./GerenciarTags.css";

function getContraste(hex = "#666666") {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "#666666");
  if (!m) return "#fff";
  const r = parseInt(m[1], 16),
    g = parseInt(m[2], 16),
    b = parseInt(m[3], 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? "#111" : "#fff";
}

export default function GerenciarTags() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  // criar nova tag
  const [novaNome, setNovaNome] = useState("");
  const [novaValor, setNovaValor] = useState("");
  const [novaCor, setNovaCor] = useState("#666666");

  // edição inline
  const [editId, setEditId] = useState(null);
  const [editNome, setEditNome] = useState("");
  const [editValor, setEditValor] = useState("");
  const [editCor, setEditCor] = useState("#666666");

  // 🔹 filtro por intervalo de datas
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const moedaBR = useMemo(
    () =>
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
      }),
    []
  );

  const normalizaHex = (s = "") => {
    s = (s || "").toUpperCase().replace(/[^#0-9A-F]/g, "");
    if (!s.startsWith("#")) s = "#" + s;
    return s.slice(0, 7);
  };


  const dataBR = (str) => {
    if (!str) return "-";
    const [data, hora] = String(str).split(" ");
    const [yyyy, mm, dd] = data.split("-");
    return hora ? `${dd}/${mm}/${yyyy} ${hora.slice(0, 5)}` : `${dd}/${mm}/${yyyy}`;
  };

  const buildQuery = () => {
    const qs = new URLSearchParams();
    // endpoint suporta ?lista=1 (listagem completa com qtd_leads/valor/total)
    qs.set("lista", "1");
    if (dateFrom) qs.set("from", dateFrom); // yyyy-mm-dd
    if (dateTo) qs.set("to", dateTo); // yyyy-mm-dd
    return qs.toString();
  };

  const fetchTags = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `https://welaunchyou.com.br/crm-wly/tags.php?${buildQuery()}`,
        { credentials: "include" }
      );
      const data = await res.json();

      if (Array.isArray(data.tags)) {
        setTags(
          data.tags.map((t) => ({
            ...t,
            valor: Number(t.valor ?? 0),
            qtd_leads: Number(t.qtd_leads ?? 0),
            cor: t.cor || "#666666",
          }))
        );
      } else if (Array.isArray(data)) {
        setTags(
          data.map((t) => ({
            ...t,
            valor: 0,
            qtd_leads: 0,
            cor: t.cor || "#666666",
          }))
        );
      } else {
        setTags([]);
      }
    } catch (e) {
      console.error("Erro ao carregar tags:", e);
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aplicarFiltro = () => fetchTags();
  const limparFiltro = () => {
    setDateFrom("");
    setDateTo("");
    // re-carrega lista sem filtro
    setTimeout(fetchTags, 0);
  };

  const criarTag = async () => {
    const nome = novaNome.trim();
    const valor = Number(String(novaValor).replace(",", ".")) || 0;
    if (!nome) return;

    try {
      const res = await fetch("https://welaunchyou.com.br/crm-wly/tags.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome, valor, cor: novaCor }),
      });
      const data = await res.json();
      if (data.success) {
        setNovaNome("");
        setNovaValor("");
        setNovaCor("#666666");
        fetchTags();
      } else {
        alert(data.error || "Erro ao criar tag.");
      }
    } catch (e) {
      alert("Erro de conexão ao criar tag.");
    }
  };

  const iniciarEdicao = (tag) => {
    setEditId(tag.id);
    setEditNome(tag.nome);
    setEditValor(String(tag.valor ?? 0).replace(".", ","));
    setEditCor(tag.cor || "#666666");
  };

  const cancelarEdicao = () => {
    setEditId(null);
    setEditNome("");
    setEditValor("");
    setEditCor("#666666");
  };

  const salvarEdicao = async (id) => {
    const nome = editNome.trim();
    const valor = Number(String(editValor).replace(",", ".")) || 0;
    if (!nome) {
      alert("Nome da tag é obrigatório.");
      return;
    }

    try {
      const res = await fetch("https://welaunchyou.com.br/crm-wly/tags.php", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, nome, valor, cor: editCor }),
      });
      const data = await res.json();
      if (data.success) {
        cancelarEdicao();
        fetchTags();
      } else {
        alert(data.error || "Erro ao atualizar tag.");
      }
    } catch (e) {
      alert("Erro de conexão ao atualizar tag.");
    }
  };

  const excluirTag = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta tag?")) return;

    try {
      const res = await fetch("https://welaunchyou.com.br/crm-wly/tags.php", {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (data.success) {
        setTags((prev) => prev.filter((t) => t.id !== id));
      } else {
        alert(data.error || "Erro ao excluir tag.");
      }
    } catch (e) {
      alert("Erro de conexão ao excluir tag.");
    }
  };

  return (
    <div className="tags-container-titulo">
      <h2 style={{ borderBottom: "none" }}>Gerenciar Tags</h2>

      <div className="tags-container">
        {/* 🔹 Filtro por intervalo de datas (lead_tags.created_at) */}
        <div className="tags-filter-card dark">
          <div className="filter-row">
            <label className="filter-label">
              <span>De</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </label>

            <label className="filter-label">
              <span>Até</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </label>

            <div className="filter-actions">
              <button onClick={aplicarFiltro} className="btn-primario">
                Filtrar
              </button>
              <button onClick={limparFiltro} className="btn-neutro">
                Limpar
              </button>
            </div>
          </div>

          <p className="filter-hint">
            A coluna <strong>“Qtd. Leads”</strong> indica quantos leads
            receberam essa tag dentro do período selecionado (usando o campo{" "}
            <code>associado em</code>).
          </p>
        </div>

        {/* Form de criação */}
        <div className="tags-create-card">
          <div className="create-grid">
            <input
            type="text"
            placeholder="Nome da nova tag"
            value={novaNome}
            onChange={(e) => setNovaNome(e.target.value)}
          />

          <input
            type="text"
            inputMode="decimal"
            placeholder="Valor unitário (R$)"
            value={novaValor}
            onChange={(e) => setNovaValor(e.target.value)}
          />

          <div className="color-picker-wrap">
            <input
              type="color"
              className="color-swatch"
              value={novaCor}
              onChange={(e) => setNovaCor(e.target.value)}
              title="Escolher cor"
            />
            <input
              className="hex-input"
              value={novaCor}
              onChange={(e) => setNovaCor(normalizaHex(e.target.value))}
              maxLength={7}
              spellCheck={false}
            />
          </div>

          <button className="btn-criar" onClick={criarTag}>Criar</button>

          </div>
        </div>

        {/* Tabela */}
        <div className="tags-table-wrap">
          {loading ? (
            <p>Carregando...</p>
          ) : tags.length === 0 ? (
            <p>Nenhuma tag encontrada.</p>
          ) : (
            <table className="fila-tabela tags-table">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>ID</th>
                  <th>Tag</th>
                  <th style={{ width: 130 }}>Cor</th>
                  <th style={{ width: 220 }}>Criada em</th>
                  <th style={{ width: 130 }}>Qtd. Leads</th>
                  <th style={{ width: 160 }}>Valor unitário</th>
                  <th style={{ width: 170 }}>Total</th>
                  <th style={{ width: 80, textAlign: "center" }}>Leads</th>
                  <th style={{ width: 110, textAlign: "center" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {tags.map((t) => {
                  const total = (t.qtd_leads || 0) * (t.valor || 0);
                  const isEditing = editId === t.id;

                  return (
                    <tr key={t.id}>
                      <td>{t.id}</td>

                      {/* Nome com ícone */}
                      <td className="tag-name-cell">
                        <TagIcon size={16} className="tag-icon" />
                        {isEditing ? (
                          <input
                            className="inline-input"
                            value={editNome}
                            onChange={(e) => setEditNome(e.target.value)}
                          />
                        ) : (
                          <span>{t.nome}</span>
                        )}
                      </td>

                      {/* Cor */}
                      <td>
                        {isEditing ? (
                            <div className="color-picker-wrap">
                              <input
                                type="color"
                                className="color-swatch"
                                value={editCor}
                                onChange={(e) => setEditCor(e.target.value)}
                                title="Escolher cor"
                              />
                              <input
                                className="hex-input"
                                value={editCor}
                                onChange={(e) => setEditCor(normalizaHex(e.target.value))}
                                maxLength={7}
                                spellCheck={false}
                              />
                            </div>
                          ) : (
                            <span
                              className="tag-badge"
                              style={{
                                backgroundColor: t.cor || "#666666",
                                color: getContraste(t.cor || "#666666"),
                              }}
                              title={t.cor || "#666666"}
                            >
                              {t.cor || "#666666"}
                            </span>
                          )}

                      </td>

                      {/* Data criação da TAG */}
                      <td>{dataBR(t.created_at)}</td>

                      {/* Quantidade de leads (no período) */}
                      <td>{t.qtd_leads ?? 0}</td>

                      {/* Valor unitário */}
                      <td>
                        {isEditing ? (
                          <input
                            className="inline-input right"
                            inputMode="decimal"
                            value={editValor}
                            onChange={(e) => setEditValor(e.target.value)}
                          />
                        ) : (
                          moedaBR.format(Number(t.valor || 0))
                        )}
                      </td>

                      {/* Total */}
                      <td>{moedaBR.format(Number(total || 0))}</td>

                      {/* Lupa para a listagem filtrada (respeita o período do card) */}
                      <td className="center">
                        {(() => {
                          const qs = new URLSearchParams();
                          qs.set("tags", String(t.id)); 
                          if (dateFrom) qs.set("data_inicio", dateFrom);
                          if (dateTo)   qs.set("data_fim", dateTo);
                          const href = `/listagem-leads?${qs.toString()}`;

                          return (
                            <Link
                              to={href}
                              title="Ver leads desta tag"
                              className="btn-icon"
                            >
                              <Search size={22} />
                            </Link>
                          );
                        })()}
                      </td>

                      {/* Lápis + Lixeira */}
                      <td className="center">
                        {isEditing ? (
                          <>
                            <button
                              className="btn-small success"
                              onClick={() => salvarEdicao(t.id)}
                            >
                              Salvar
                            </button>
                            <button
                              className="btn-small neutral"
                              onClick={cancelarEdicao}
                            >
                              Cancelar
                            </button>
                          </>
                        ) : (
                          <div className="acoes-botoes center">
                            <button
                              className="btn-icon"
                              title="Editar"
                              onClick={() => iniciarEdicao(t)}
                            >
                              <Pencil size={18} />
                            </button>
                            <button
                              className="btn-icon danger"
                              title="Excluir"
                              onClick={() => excluirTag(t.id)}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
